'use client';

import { useId } from 'react';
import { PieChart as RechartsPie, Pie, Cell, Customized } from 'recharts';
import { cn } from '../lib/utils';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  type ChartConfig,
} from '../chart';

export interface PieSlice {
  key: string;
  label: string;
  value: number;
  color: string;
}

interface PieChartProps {
  data: PieSlice[];
  showLegend?: boolean;
  gradient?: boolean;
  height?: number;
  className?: string;
}

export function PieChart({
  data,
  showLegend = true,
  gradient = true,
  height,
  className,
}: PieChartProps) {
  const uid = useId().replace(/:/g, '');

  const config: ChartConfig = Object.fromEntries(
    data.map((d) => [d.key, { label: d.label, color: d.color }]),
  );

  const GradientDefs = () => (
    <defs>
      {data.map((d) => (
        <linearGradient key={d.key} id={`${uid}-grad-${d.key}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={d.color} stopOpacity={1} />
          <stop offset="100%" stopColor={d.color} stopOpacity={0.45} />
        </linearGradient>
      ))}
    </defs>
  );

  const LegendContent = () => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '4px 16px',
        paddingTop: 8,
      }}
    >
      {data.map((slice) => (
        <div
          key={slice.key}
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
        >
          <span
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: 2,
              background: slice.color,
              flexShrink: 0,
            }}
          />
          <span className="text-[var(--color-text-secondary,#71717a)]">{slice.label}</span>
          <span className="font-bold text-[var(--color-text-primary,#18181b)]">{slice.value}</span>
        </div>
      ))}
    </div>
  );

  return (
    <ChartContainer
      config={config}
      className={cn('w-full aspect-auto!', className)}
      style={height ? { height } : undefined}
    >
      <RechartsPie>
        {gradient && <Customized component={GradientDefs} />}
        <Pie data={data} dataKey="value" nameKey="key" cx="50%" cy="50%" outerRadius="80%">
          {data.map((entry) => (
            <Cell
              key={entry.key}
              fill={gradient ? `url(#${uid}-grad-${entry.key})` : `var(--color-${entry.key})`}
            />
          ))}
        </Pie>
        <ChartTooltip content={<ChartTooltipContent nameKey="key" />} />
        {showLegend && <ChartLegend content={() => <LegendContent />} />}
      </RechartsPie>
    </ChartContainer>
  );
}
